import { FC, useEffect, useState, useCallback, useRef } from 'react'
import { Modal } from '../../pages/Gallery/Gallery'
import { useGlobalContext } from '../../GlobalContext'
import './Menu.css'
import { motion } from 'framer-motion'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL as string
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY as string
const REST_BASE = `${SUPABASE_URL}/rest/v1`
const REST_HEADERS: Record<string, string> = {
   apikey: SUPABASE_ANON_KEY,
   Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
}
const BACKEND_BASE = ''

interface MainCategory {
   id: number
   nameTr: string
   nameEn: string
   isActive: boolean
   orderIndex: number
}

interface SubCategory {
   id: number
   nameTr: string
   nameEn: string
   mainCategoryId: number
   isActive: boolean
   orderIndex: number
}

interface Product {
   id: number
   nameTr: string
   nameEn: string
   descriptionTr: string
   descriptionEn: string
   flavorTr: string
   flavorEn: string
   price: string
   mainCategoryId?: number
   subCategoryId?: number
   isActive: boolean
   allergenInfoTr?: string
   allergenInfoEn?: string
   isNew: boolean
   imageUrl?: string
   orderIndex: number
}

interface MontlyCalendarImage {
   id: number
   imageUrl: string
}

const Menu: FC = () => {
   const [products, setProducts] = useState<Product[]>([])
   const [mainCategories, setMainCategories] = useState<MainCategory[]>([])
   const [subCategories, setSubCategories] = useState<SubCategory[]>([])
   const [montlyCalendarImage, setMontlyCalendarImage] =
      useState<MontlyCalendarImage | null>(null)
   const [loading, setLoading] = useState(true)
   const [error, setError] = useState<string | null>(null)
   const [showMonthlyCalendar, setShowMonthlyCalendar] = useState(false)
   const { language: currentLanguage } = useGlobalContext()
   const [activeCategory, setActiveCategory] = useState<string>('')
   const [expandedSubcategories, setExpandedSubcategories] = useState<
      Set<number>
   >(new Set())
   const [showCategoryBar, setShowCategoryBar] = useState(false)
   const categoryScrollContainerRef = useRef<HTMLDivElement | null>(null)
   const categoryButtonRefs = useRef<Record<string, HTMLButtonElement | null>>(
      {}
   )
   const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})
   const isManualScroll = useRef(false)

   // Veri çekme - MenuAdmin'deki gibi
   const normalizeProduct = (p: any): Product => ({
      id: p.id,
      nameTr: p.name_tr ?? '',
      nameEn: p.name_en ?? '',
      descriptionTr: p.description_tr ?? '',
      descriptionEn: p.description_en ?? '',
      flavorTr: p.flavor_tr ?? '',
      flavorEn: p.flavor_en ?? '',
      price: p.price ?? '',
      mainCategoryId: p.maincategoryid,
      subCategoryId: p.subcategoryid,
      isActive: p.is_active ?? true,
      allergenInfoTr: p.allergen_info_tr,
      allergenInfoEn: p.allergen_info_en,
      isNew: p.is_new ?? false,
      imageUrl: p.image_url,
      orderIndex: p.orderindex ?? 0,
   })
   const normalizeMain = (m: any): MainCategory => ({
      id: m.id,
      nameTr: m.name_tr ?? '',
      nameEn: m.name_en ?? '',
      isActive: m.is_active ?? true,
      orderIndex: m.orderindex ?? 0,
   })
   const normalizeSub = (s: any): SubCategory => ({
      id: s.id,
      nameTr: s.name_tr ?? '',
      nameEn: s.name_en ?? '',
      mainCategoryId: s.maincategoryid,
      isActive: s.is_active ?? true,
      orderIndex: s.orderindex ?? 0,
   })
   const normalizeMonthly = (m: any): MontlyCalendarImage => ({
      id: m.id,
      imageUrl: m.image_url ?? '',
   })
   const fetchAll = async () => {
      setLoading(true)
      try {
         const [
            productsRes,
            mainCategoriesRes,
            subCategoriesRes,
            montlyCalendarRes,
         ] = await Promise.all([
            fetch(
               `${REST_BASE}/products?select=*&is_active=eq.true&order=orderindex`,
               { headers: REST_HEADERS }
            ),
            fetch(`${REST_BASE}/maincategories?select=*&order=orderindex`, {
               headers: REST_HEADERS,
            }),
            fetch(
               `${REST_BASE}/subcategories?select=*&order=maincategoryid,orderindex`,
               { headers: REST_HEADERS }
            ),
            fetch(
               `${REST_BASE}/montlycalendarimages?select=*&order=id.desc&limit=1`,
               { headers: REST_HEADERS }
            ),
         ])

         const [
            productsRaw,
            mainCategoriesRaw,
            subCategoriesRaw,
            montlyCalendarRaw,
         ] = await Promise.all([
            productsRes.json(),
            mainCategoriesRes.json(),
            subCategoriesRes.json(),
            montlyCalendarRes.json(),
         ])

         const productsData: Product[] = Array.isArray(productsRaw)
            ? productsRaw.map(normalizeProduct)
            : []
         const mainCategoriesData: MainCategory[] = Array.isArray(
            mainCategoriesRaw
         )
            ? mainCategoriesRaw.map(normalizeMain)
            : []
         const subCategoriesData: SubCategory[] = Array.isArray(
            subCategoriesRaw
         )
            ? subCategoriesRaw.map(normalizeSub)
            : []
         const montlyCalendarData: MontlyCalendarImage[] = Array.isArray(
            montlyCalendarRaw
         )
            ? montlyCalendarRaw.map(normalizeMonthly)
            : []

         // orderIndex'e göre sırala
         const sortedProducts = productsData.sort(
            (a: Product, b: Product) => a.orderIndex - b.orderIndex
         )
         const sortedMainCategories = mainCategoriesData.sort(
            (a: MainCategory, b: MainCategory) => a.orderIndex - b.orderIndex
         )
         const sortedSubCategories = subCategoriesData.sort(
            (a: SubCategory, b: SubCategory) => a.orderIndex - b.orderIndex
         )

         setProducts(sortedProducts)
         setMainCategories(sortedMainCategories)
         setSubCategories(sortedSubCategories)
         setMontlyCalendarImage(montlyCalendarData[0]) // İlk resmi ayarla

         // İlk kategoriyi aktif yap
         if (mainCategoriesData.length > 0) {
            setActiveCategory(mainCategoriesData[0].nameTr)
         }
      } catch (err) {
         console.error('Error fetching data:', err)
         setError('Veri yüklenirken hata oluştu')
      } finally {
         setLoading(false)
      }
   }

   useEffect(() => {
      fetchAll()
   }, [])

   // Dil değiştirme Navbar'daki .language-menu-item üzerinden yönetiliyor

   // Show category bar only after slight scroll
   useEffect(() => {
      const onScroll = () => {
         setShowCategoryBar(window.scrollY > 10)
      }
      onScroll()
      window.addEventListener('scroll', onScroll)
      return () => window.removeEventListener('scroll', onScroll)
   }, [])

   // Monthly calendar popup kontrolü - toggle yerine açık/kapalı ayrı
   const openMonthlyCalendar = useCallback(() => {
      setShowMonthlyCalendar(true)
   }, [])
   const closeMonthlyCalendar = useCallback(() => {
      setShowMonthlyCalendar(false)
   }, [])

   // Kategori değiştirme
   const handleCategoryChange = (categoryName: string) => {
      isManualScroll.current = true
      setActiveCategory(categoryName)
      const element = sectionRefs.current[categoryName]
      if (element) {
         const stickyHeaderHeight = window.innerWidth <= 768 ? 70 : 100
         const elementPosition =
            element.getBoundingClientRect().top + window.pageYOffset
         window.scrollTo({
            top: elementPosition - stickyHeaderHeight,
            behavior: 'smooth',
         })
      }
      setTimeout(() => {
         isManualScroll.current = false
      }, 800)
   }

   // Aktif kategori değiştiğinde üstteki kategori barını da ortala
   useEffect(() => {
      if (!activeCategory) return
      const buttonEl = categoryButtonRefs.current[activeCategory]
      if (buttonEl) {
         try {
            // Bazı tarayıcılarda inline:center desteklenir
            buttonEl.scrollIntoView({
               behavior: 'smooth',
               inline: 'center',
               block: 'nearest',
            } as ScrollIntoViewOptions)
            return
         } catch {}
      }
      const container = categoryScrollContainerRef.current
      if (container && buttonEl) {
         const containerRect = container.getBoundingClientRect()
         const buttonRect = buttonEl.getBoundingClientRect()
         const currentLeft = container.scrollLeft
         const offsetLeft = buttonRect.left - containerRect.left + currentLeft
         const targetLeft =
            offsetLeft - container.clientWidth / 2 + buttonEl.clientWidth / 2
         container.scrollTo({
            left: Math.max(0, targetLeft),
            behavior: 'smooth',
         })
      }
   }, [activeCategory])

   // Scroll ettikçe en yakın kategoriye göre aktif kategoriyi güncelle
   useEffect(() => {
      const handleScroll = () => {
         if (isManualScroll.current) return
         let closestCategory = ''
         let minDistance = Infinity
         const offset = window.innerWidth <= 768 ? 70 : 100
         mainCategories.forEach(cat => {
            const el = sectionRefs.current[cat.nameTr]
            if (el) {
               const rect = el.getBoundingClientRect()
               const distance = Math.abs(rect.top - offset)
               if (distance < minDistance) {
                  minDistance = distance
                  closestCategory = cat.nameTr
               }
            }
         })
         if (closestCategory && closestCategory !== activeCategory) {
            setActiveCategory(closestCategory)
         }
      }
      window.addEventListener('scroll', handleScroll)
      return () => window.removeEventListener('scroll', handleScroll)
   }, [activeCategory, mainCategories])

   // Subcategory aç/kapat
   const toggleSubcategory = (subcategoryId: number) => {
      setExpandedSubcategories(prev => {
         const newSet = new Set(prev)
         if (newSet.has(subcategoryId)) {
            newSet.delete(subcategoryId)
         } else {
            newSet.add(subcategoryId)
         }
         return newSet
      })
   }

   if (loading) {
      return (
         <div className="flex items-center justify-center min-h-screen">
            <div className="text-xl">Yükleniyor...</div>
         </div>
      )
   }

   if (error) {
      return (
         <div className="flex items-center justify-center min-h-screen">
            <div className="text-xl text-red-500">{error}</div>
         </div>
      )
   }

   return (
      <div className="relative">
         {/* Dil değişimi Navbar'daki .language-menu-item ile yapılır */}

         {/* Sticky Category Navigation Bar */}
         <div
            className={`sticky-category-bar-wrapper ${
               showCategoryBar ? 'visible' : 'hidden'
            }`}>
            <div
               className="category-scroll-container"
               ref={categoryScrollContainerRef}>
               {mainCategories.map(category => (
                  <button
                     key={category.id}
                     className={`category-button ${
                        activeCategory === category.nameTr ? 'active' : ''
                     }`}
                     ref={el => {
                        categoryButtonRefs.current[category.nameTr] = el
                     }}
                     onClick={() => handleCategoryChange(category.nameTr)}>
                     {currentLanguage === 'tr'
                        ? category.nameTr
                        : category.nameEn}
                  </button>
               ))}
            </div>
         </div>

         {/* Monthly Calendar Popup Section */}
         <div className="menu-section-popup">
            <div className="menu-item" onClick={openMonthlyCalendar}>
               <div className="menu-item-details">
                  <h3 className="menu-item-popup-name">
                     {currentLanguage === 'tr'
                        ? "🎉 BOBO FRANCO'DA BU AY 🎉"
                        : '🎉 THIS MONTH AT BOBO FRANCO 🎉'}
                  </h3>
               </div>
            </div>
         </div>

         {/* Monthly Calendar Popup */}
         {showMonthlyCalendar && montlyCalendarImage && (
            <Modal
               imgSrc={`${BACKEND_BASE}${montlyCalendarImage.imageUrl}`}
               onClose={closeMonthlyCalendar}
               onNext={() => {}}
               onPrev={() => {}}
               hasNext={false}
               hasPrev={false}
            />
         )}

         {/* Menu Content */}
         <div className="menu-container">
            <div className="menu-content">
               {mainCategories.map(mainCategory => (
                  <div
                     key={mainCategory.id}
                     id={`category-${mainCategory.nameTr}`}
                     ref={el => (sectionRefs.current[mainCategory.nameTr] = el)}
                     className="menu-section">
                     <h2 className="menu-category-title">
                        {currentLanguage === 'tr'
                           ? mainCategory.nameTr
                           : mainCategory.nameEn}
                     </h2>

                     {/* SubCategories varsa */}
                     {subCategories.filter(
                        sub => sub.mainCategoryId === mainCategory.id
                     ).length > 0 ? (
                        subCategories
                           .filter(
                              sub => sub.mainCategoryId === mainCategory.id
                           )
                           .map(subCategory => {
                              const isExpanded = expandedSubcategories.has(
                                 subCategory.id
                              )
                              const subcategoryProducts = products
                                 .filter(
                                    product =>
                                       product.subCategoryId === subCategory.id
                                 )
                                 .sort((a, b) => a.orderIndex - b.orderIndex)

                              return (
                                 <div
                                    key={subCategory.id}
                                    className={`menu-subcategory-section ${
                                       isExpanded ? 'expanded' : ''
                                    }`}>
                                    <button
                                       className={`menu-subcategory-title ${
                                          isExpanded ? 'expanded' : ''
                                       }`}
                                       onClick={() =>
                                          toggleSubcategory(subCategory.id)
                                       }>
                                       {currentLanguage === 'tr'
                                          ? subCategory.nameTr
                                          : subCategory.nameEn}
                                       <motion.span
                                          className="dropdown-arrow"
                                          initial={false}
                                          animate={{
                                             rotate: isExpanded ? 180 : 0,
                                          }}
                                          transition={{
                                             duration: 0.3,
                                             ease: 'easeInOut',
                                          }}>
                                          ▼
                                       </motion.span>
                                    </button>

                                    <div
                                       className={`subcategory-items ${
                                          isExpanded ? 'expanded' : ''
                                       }`}>
                                       {isExpanded && (
                                          <div className="menu-item-container">
                                             {subcategoryProducts.map(
                                                product => (
                                                   <div
                                                      key={product.id}
                                                      className="menu-item">
                                                      {product.isNew && (
                                                         <div className="new-badge">
                                                            {currentLanguage ===
                                                            'tr'
                                                               ? 'Yeni'
                                                               : 'New'}
                                                         </div>
                                                      )}

                                                      <div className="menu-item-details">
                                                         <h4 className="menu-item-name">
                                                            {currentLanguage ===
                                                            'tr'
                                                               ? product.nameTr
                                                               : product.nameEn}
                                                         </h4>

                                                         {product.flavorTr && (
                                                            <p className="menu-item-flavor">
                                                               {currentLanguage ===
                                                               'tr'
                                                                  ? product.flavorTr
                                                                  : product.flavorEn}
                                                            </p>
                                                         )}

                                                         {product.descriptionTr && (
                                                            <p className="menu-item-description">
                                                               {currentLanguage ===
                                                               'tr'
                                                                  ? product.descriptionTr
                                                                  : product.descriptionEn}
                                                            </p>
                                                         )}

                                                         {product.allergenInfoTr && (
                                                            <p className="menu-item-allergen">
                                                               {currentLanguage ===
                                                               'tr'
                                                                  ? product.allergenInfoTr
                                                                  : product.allergenInfoEn}
                                                            </p>
                                                         )}
                                                      </div>

                                                      <span className="menu-item-price">
                                                         {product.price}
                                                      </span>
                                                   </div>
                                                )
                                             )}
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              )
                           })
                     ) : (
                        /* SubCategory yoksa direkt products göster */
                        <div className="menu-item-container">
                           {products
                              .filter(
                                 product =>
                                    product.mainCategoryId ===
                                       mainCategory.id && !product.subCategoryId
                              )
                              .sort((a, b) => a.orderIndex - b.orderIndex)
                              .map(product => (
                                 <div key={product.id} className="menu-item">
                                    {product.isNew && (
                                       <div className="new-badge">
                                          {currentLanguage === 'tr'
                                             ? 'Yeni'
                                             : 'New'}
                                       </div>
                                    )}

                                    <div className="menu-item-details">
                                       <h4 className="menu-item-name">
                                          {currentLanguage === 'tr'
                                             ? product.nameTr
                                             : product.nameEn}
                                       </h4>

                                       {product.flavorTr && (
                                          <p className="menu-item-flavor">
                                             {currentLanguage === 'tr'
                                                ? product.flavorTr
                                                : product.flavorEn}
                                          </p>
                                       )}

                                       {product.descriptionTr && (
                                          <p className="menu-item-description">
                                             {currentLanguage === 'tr'
                                                ? product.descriptionTr
                                                : product.descriptionEn}
                                          </p>
                                       )}

                                       {product.allergenInfoTr && (
                                          <p className="menu-item-allergen">
                                             {currentLanguage === 'tr'
                                                ? product.allergenInfoTr
                                                : product.allergenInfoEn}
                                          </p>
                                       )}
                                    </div>

                                    <span className="menu-item-price">
                                       {product.price}
                                    </span>
                                 </div>
                              ))}
                        </div>
                     )}
                  </div>
               ))}
            </div>
         </div>
      </div>
   )
}

export default Menu
